import React, { useMemo } from "react";
import { useSelector } from "react-redux";

import { getTevaCopayFormInitialValues } from "./form/initialValues";
import { SaveButton } from "../../header";
import useBaseEEConfiguration from "../../hooks/useBaseEEConfiguration";
import { applyTrimFields } from "../../utils/updateFormData";
import renderReactFromJson from "../../utils/renderReactFromJson";
import formJson from "./form/TevaCopayFormJson.json";
import { CompleteFormButton } from "../../CompleteFormButton";
import { HEADER_SUBMIT_BUTTON_TYPES } from "../../constants";
import { SignerTypes } from "../../../../../constant/ESign";
import { DATE_FORMAT, dateFormatter } from "../../../../../utils/date";
import { selectSelectedAssistanceProgramById } from "../../../../../store/selector";
import { SUPPORT_PROGRAM_PIDS } from "../../../../../utils/supportPrograms";

/** Removes the Copay Eligibility section from the form schema for support programs. */
const buildSupportFormJson = () => ({
  ...formJson,
  formSections: formJson.formSections.filter((section) => section.sectionName !== "copayEligibility")
});

export const useTevaCopayConfiguration = () => {
  const vendorName = "teva";
  const rootPath = "application_form_editor.teva.copay";

  const selectedAssistanceProgramById = useSelector(selectSelectedAssistanceProgramById);
  const isSupportProgram = SUPPORT_PROGRAM_PIDS.includes(selectedAssistanceProgramById?.pid);
  const eePlanType = isSupportProgram ? "support" : "copay";
  const activeFormJson = useMemo(() => (isSupportProgram ? buildSupportFormJson() : formJson), [isSupportProgram]);

  const baseConfig = useBaseEEConfiguration({ vendorName, eePlanType, rootPath, formJson: activeFormJson });

  const tevaSpecificConfig = {
    activeFormSection: "",
    getFormInitialValues: ({ applicationForm }) =>
      getTevaCopayFormInitialValues({ applicationForm, formJson: activeFormJson }),
    getFormFromJson: (form) => renderReactFromJson(form, activeFormJson),
    saveButton: (props) => (
      <SaveButton onSave={(form) => applyTrimFields(form)} id={"EE_teva_copay_save_form"} {...props} />
    ),
    afterSubmissionPayload: () => ({}),
    applicationFormHeaderSubmission: (props) => <CompleteFormButton id={"EE_complete_button"} {...props} />,
    headerSubmitButtonType: HEADER_SUBMIT_BUTTON_TYPES.COMPLETE_FORM,

    getSignatureWidgetSettingsBySignerType: (signerType) => {
      const signatureSettings = {
        [SignerTypes.PATIENT]: {
          editable: true,
          titleTranslationKey: "patientApplications.enroll.electronic_signatures.signature_step.signers.patient",
          shouldShowCaregiverIndication: false,
          shouldUseRemoteRequestCheckboxes: false
        }
      };
      return signatureSettings[signerType];
    },
    getEnrollResultData: ({ application }) => ({
      bin: application.bin,
      pcn: application.pcn,
      group: application.group,
      member_id: application.memberId,
      ...(application.validThrough && {
        expiration_date: dateFormatter(application.validThrough)
      })
    }),
    getAdditionalResultCardInformation: false,
    getApprovedResultDescription: () => <></>,

    successMessage: (t, submittedDate, shouldAutoSubmit) =>
      t(
        !shouldAutoSubmit
          ? "patientApplications.enroll.application_form_submission.submit_completed_teva"
          : "patientApplications.enroll.application_form_submission.auto_submit_succeeded",
        {
          submitDate: dateFormatter(submittedDate, DATE_FORMAT)
        }
      ),
    noCardResultText: (t) => t("patientApplications.enroll.enroll_result.teva_enrollment_message"),
    shouldShowPdfInSecondStep: true
  };

  return {
    ...baseConfig,
    ...tevaSpecificConfig
  };
};

export default useTevaCopayConfiguration;
